import { useState } from "react";
import { AuthWrapper } from "../components/AuthWrapper";
import { GoogleButton } from "../components/GoogleButton";
import {
  Button,
  Center,
  FormControl,
  Input,
  Link,
  Divider,
  Alert,
  AlertIcon,
  AlertDescription,
} from "@chakra-ui/react";

import http from "../connection/connect";

import { navigateToUserStoreSignedIn, safeNext, startSession } from "../helpers/global";
import { trackEvent } from "../helpers/tracking";
import { withTranslation } from 'react-i18next';

const Login = (props) => {
  const { t } = props;
  const [isLoading, setLoading] = useState(false);

  //form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  //error state — also set when "Continue with Google" came back unsuccessful
  const [err, setError] = useState(() => {
    const code = new URLSearchParams(window.location.search).get("google_error");
    if (!code) return "";
    return code === "unverified"
      ? t("google.errorUnverified", "Your Google account's e-mail is not verified. Verify it with Google or sign up with e-mail.")
      : code === "cancelled"
        ? t("google.errorCancelled", "Google sign-in was cancelled.")
        : t("google.errorFailed", "Google sign-in did not complete. Please try again.");
  });

  //validation state
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  //validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    let isValid = true;

    // Reset errors
    setEmailError("");
    setPasswordError("");
    setError("");

    // Validate email
    if (!email.trim()) {
      setEmailError(t('loginPage.emailRequired') || "Email is required");
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError(t('loginPage.emailInvalid') || "Please enter a valid email address");
      isValid = false;
    }

    // Validate password
    if (!password.trim()) {
      setPasswordError(t('loginPage.passwordRequired') || "Password is required");
      isValid = false;
    }

    return isValid;
  };

  //form values
  const formValues = {
    email,
    password,
  };

  //form submit function
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submitting
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await http.post("api/auth/login", formValues);
      if (response.data.status === 200) {
        setLoading(false);
        // Clears whatever the previous account left behind before writing the
        // new session — see startSession.
        startSession(response.data);
        // Fresh id per login: reusing the user id would make Meta dedupe
        // repeat logins within 48h.
        trackEvent("login");
        if (window.location.origin.includes("storak.local")) {
          const userAction = window.confirm(
            "Would you like to redirect to current react app ?"
          );
          if (userAction) {
            window.location.href = window.location.origin;
            return;
          }
        }
        // Signs them into the store as well, so the portal password is the
        // only one they type.
        const next = safeNext();
        if (next) {
          window.location.href = next;
          return;
        }
        await navigateToUserStoreSignedIn();
      } else {
        setLoading(false);
        throw response;
      }
    } catch (error) {
      setLoading(false);
      const errorData = error.response || error.data;
      if (errorData) {
        // Handle different HTTP status codes
        if (errorData.status === 401 || errorData?.status === 400) {
          setError(t('loginPage.invalidCredentials') || "Invalid email or password");
        } else if (errorData.status === 429) {
          setError(t('loginPage.tooManyAttempts') || "Too many login attempts. Please try again later.");
        } else {
          setError(errorData.data.message || errorData.data.msg || t('loginPage.loginFailed'));
        }
      }
    }
  };

  return (
    <AuthWrapper
      header={t('loginPage.header')}
      subtitle={t('loginPage.subtitle', 'Sign in to manage your store, orders and assistant.')}
    >
      <GoogleButton source="login" />
      <form onSubmit={handleSubmit}>
        <FormControl pt={4}>
          {err && (
            <Alert
              status="error"
              mt={3}
              borderRadius="md"
              bg="rgba(220, 38, 38, 0.15)"
              borderColor="rgba(220, 38, 38, 0.5)"
              borderWidth="1px"
              color="white"
            >
              <AlertIcon color="rgba(252, 165, 165, 1)" />
              <AlertDescription color="white">{err}</AlertDescription>
            </Alert>
          )}

          <Input
            mt={5}
            id="email"
            placeholder={t('loginPage.emailInputPlaceholder')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            isInvalid={!!emailError}
            bg="rgba(255, 255, 255, 0.1)"
            borderColor={emailError ? "red.500" : "rgba(255, 255, 255, 0.3)"}
            color="white"
            _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
            _hover={{ borderColor: emailError ? "red.500" : "rgba(255, 255, 255, 0.5)" }}
            _focus={{
              borderColor: emailError ? "red.500" : "rgba(255, 255, 255, 0.8)",
              bg: "rgba(255, 255, 255, 0.15)",
              boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.3)"
            }}
          />
          {emailError && (
            <Alert
              status="error"
              mt={2}
              borderRadius="md"
              size="sm"
              bg="rgba(220, 38, 38, 0.15)"
              borderColor="rgba(220, 38, 38, 0.5)"
              borderWidth="1px"
              color="white"
            >
              <AlertIcon color="rgba(252, 165, 165, 1)" />
              <AlertDescription fontSize="sm" color="white">{emailError}</AlertDescription>
            </Alert>
          )}

          <Input
            mt={5}
            id="password"
            placeholder={t('loginPage.passwordInputPlaceholder')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            isInvalid={!!passwordError}
            bg="rgba(255, 255, 255, 0.1)"
            borderColor={passwordError ? "red.500" : "rgba(255, 255, 255, 0.3)"}
            color="white"
            _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
            _hover={{ borderColor: passwordError ? "red.500" : "rgba(255, 255, 255, 0.5)" }}
            _focus={{
              borderColor: passwordError ? "red.500" : "rgba(255, 255, 255, 0.8)",
              bg: "rgba(255, 255, 255, 0.15)",
              boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.3)"
            }}
          />
          {passwordError && (
            <Alert
              status="error"
              mt={2}
              borderRadius="md"
              size="sm"
              bg="rgba(220, 38, 38, 0.15)"
              borderColor="rgba(220, 38, 38, 0.5)"
              borderWidth="1px"
              color="white"
            >
              <AlertIcon color="rgba(252, 165, 165, 1)" />
              <AlertDescription fontSize="sm" color="white">{passwordError}</AlertDescription>
            </Alert>
          )}

          <Button
            isLoading={isLoading}
            loadingText={t('loginPage.loading')}
            type="submit"
            width="100%"
            mt={5}
            height="3.5rem"
            fontSize="lg"
            fontWeight="bold"
            bg="white"
            color="#001756"
            _hover={{ bg: "rgba(255, 255, 255, 0.9)" }}
            _active={{ bg: "rgba(255, 255, 255, 0.8)" }}
          >
            {t('loginPage.loginButtonText')}
          </Button>
          <Center mt={3}>
            <Link href="/register" textAlign="center" color="white" _hover={{ color: "rgba(255, 255, 255, 0.8)", textDecoration: "underline" }}>
              {t('loginPage.dontHaveAccount')}
            </Link>
          </Center>
          <Center>
            <Link href="/forgot-password" color="white" _hover={{ color: "rgba(255, 255, 255, 0.8)", textDecoration: "underline" }}>{t('loginPage.forgotPassword')}</Link>
          </Center>
        </FormControl>
      </form>
    </AuthWrapper>
  );
};

export default withTranslation()(Login);
